import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as api from './fixed-departures.api';

import type {
  CreateFixedDepartureActivityRequest,
  CreateFixedDepartureDepartureDateRequest,
  UpdateFixedDepartureDepartureDateRequest,
  CreateItineraryFromFixedDepartureRequest,
  ReseedFixedDepartureItineraryRequest,
  FixedDepartureQuotePreviewQuery,
  FixedDepartureRoomQuotePreviewRequest,
  CreateFixedDepartureCategoryRequest,
  CreateFixedDepartureVehicleSupplementRequest,
  CreateFixedDepartureFlightRequest,
  CreateFixedDepartureHotelRequest,
  CreateFixedDepartureRequest,
  CreateFixedDepartureTextRequest,
  FixedDepartureListQuery,
  SaveFixedDepartureCategoryPricesRequest,
  SaveFixedDepartureCitiesRequest,
  SaveFixedDepartureLandPackageRequest,
  UpdateFixedDepartureActivityRequest,
  UpdateFixedDepartureCategoryRequest,
  UpdateFixedDepartureVehicleSupplementRequest,
  UpdateFixedDepartureDayRequest,
  UpdateFixedDepartureFlightRequest,
  UpdateFixedDepartureHotelRequest,
  UpdateFixedDepartureRequest,
  UpdateFixedDepartureTextRequest,
} from '@utazzo/shared';

const QK = {
  list: (query: FixedDepartureListQuery) => ['fixed-departures', 'list', query] as const,
  facets: ['fixed-departures', 'facets'] as const,
  detail: (id: string) => ['fixed-departures', 'detail', id] as const,
};

/**
 * Every child mutation invalidates the parent detail query: the API returns the
 * single changed row, but the builder renders off the whole template (day count,
 * city summary, nights), so a refetch is what keeps those in sync.
 */
const useChildMutation = <TArgs, TResult>(
  fixedDepartureId: string,
  mutationFn: (args: TArgs) => Promise<TResult>,
  successMessage: string,
  errorMessage: string,
) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.detail(fixedDepartureId) });
      toast.success(successMessage);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? errorMessage),
  });
};

// ── Master ────────────────────────────────────────────────────────────────────

export const useFixedDeparturesList = (query: FixedDepartureListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => api.listFixedDepartures(query),
    staleTime: 30_000,
  });

/**
 * Facets (in-use countries + cities) for the Country / City list filters.
 * Keyed under ['fixed-departures'] so template mutations refresh it - a
 * template routed through a new city shows up in the filter automatically.
 */
export const useFixedDepartureFacets = () =>
  useQuery({
    queryKey: QK.facets,
    queryFn: api.getFixedDepartureFacets,
    staleTime: 60_000,
  });

export const useFixedDepartureDetail = (id: string | undefined) =>
  useQuery({
    queryKey: id ? QK.detail(id) : ['fixed-departures', 'detail', 'none'],
    queryFn: () => api.getFixedDeparture(id as string),
    enabled: Boolean(id),
    staleTime: 30_000,
  });

export const useCreateFixedDeparture = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateFixedDepartureRequest) => api.createFixedDeparture(payload),
    onSuccess: (fd) => {
      qc.invalidateQueries({ queryKey: ['fixed-departures', 'list'] });
      toast.success(`Created ${fd.reference}`);
    },
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to create fixed departure'),
  });
};

export const useUpdateFixedDeparture = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateFixedDepartureRequest }) =>
      api.updateFixedDeparture(id, payload),
    onSuccess: (fd) => {
      qc.invalidateQueries({ queryKey: ['fixed-departures', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(fd.id) });
      toast.success('Fixed departure updated');
    },
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to update fixed departure'),
  });
};

export const useDeleteFixedDeparture = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteFixedDeparture(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fixed-departures', 'list'] });
      toast.success('Fixed departure deleted');
    },
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to delete fixed departure'),
  });
};

export const useDuplicateFixedDeparture = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.duplicateFixedDeparture(id),
    onSuccess: (fd) => {
      qc.invalidateQueries({ queryKey: ['fixed-departures', 'list'] });
      toast.success(`Duplicated as "${fd.title}" (${fd.reference}, draft)`);
    },
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to duplicate fixed departure'),
  });
};

export const useSaveFixedDepartureCities = (id: string) =>
  useChildMutation(
    id,
    (payload: SaveFixedDepartureCitiesRequest) => api.saveFixedDepartureCities(id, payload),
    'Itinerary structure saved',
    'Failed to save the city configuration',
  );

// ── Departure dates ───────────────────────────────────────────────────────────

export const useCreateFixedDepartureDepartureDate = (id: string) =>
  useChildMutation(
    id,
    (payload: CreateFixedDepartureDepartureDateRequest) =>
      api.createFixedDepartureDepartureDate(id, payload),
    'Departure date added',
    'Failed to add the departure date',
  );

export const useUpdateFixedDepartureDepartureDate = (id: string) =>
  useChildMutation(
    id,
    ({ dateId, payload }: { dateId: string; payload: UpdateFixedDepartureDepartureDateRequest }) =>
      api.updateFixedDepartureDepartureDate(id, dateId, payload),
    'Departure date updated',
    'Failed to update the departure date',
  );

export const useDeleteFixedDepartureDepartureDate = (id: string) =>
  useChildMutation(
    id,
    (dateId: string) => api.deleteFixedDepartureDepartureDate(id, dateId),
    'Departure date removed',
    'Failed to remove the departure date',
  );

// ── Days ──────────────────────────────────────────────────────────────────────

export const useUpdateFixedDepartureDay = (id: string) =>
  useChildMutation(
    id,
    ({ dayId, payload }: { dayId: string; payload: UpdateFixedDepartureDayRequest }) =>
      api.updateFixedDepartureDay(id, dayId, payload),
    'Day saved',
    'Failed to save the day',
  );

// ── Categories (grade tiers) + price grid ─────────────────────────────────────

export const useCreateFixedDepartureCategory = (id: string) =>
  useChildMutation(
    id,
    (payload: CreateFixedDepartureCategoryRequest) => api.createFixedDepartureCategory(id, payload),
    'Tier added',
    'Failed to add the tier',
  );

export const useUpdateFixedDepartureCategory = (id: string) =>
  useChildMutation(
    id,
    ({
      categoryId,
      payload,
    }: {
      categoryId: string;
      payload: UpdateFixedDepartureCategoryRequest;
    }) => api.updateFixedDepartureCategory(id, categoryId, payload),
    'Tier updated',
    'Failed to update the tier',
  );

export const useDeleteFixedDepartureCategory = (id: string) =>
  useChildMutation(
    id,
    (categoryId: string) => api.deleteFixedDepartureCategory(id, categoryId),
    'Tier removed',
    'Failed to remove the tier',
  );

export const useReorderFixedDepartureCategories = (id: string) =>
  useChildMutation(
    id,
    (ids: string[]) => api.reorderFixedDepartureCategories(id, ids),
    'Tier order saved',
    'Failed to reorder the tiers',
  );

export const useSaveFixedDepartureCategoryPrices = (id: string) =>
  useChildMutation(
    id,
    ({
      categoryId,
      payload,
    }: {
      categoryId: string;
      payload: SaveFixedDepartureCategoryPricesRequest;
    }) => api.saveFixedDepartureCategoryPrices(id, categoryId, payload),
    'Prices saved',
    'Failed to save the prices',
  );

// ── Vehicle supplements ───────────────────────────────────────────────────────

export const useCreateFixedDepartureVehicleSupplement = (id: string) =>
  useChildMutation(
    id,
    (payload: CreateFixedDepartureVehicleSupplementRequest) =>
      api.createFixedDepartureVehicleSupplement(id, payload),
    'Supplement added',
    'Failed to add the supplement',
  );

export const useUpdateFixedDepartureVehicleSupplement = (id: string) =>
  useChildMutation(
    id,
    ({
      supplementId,
      payload,
    }: {
      supplementId: string;
      payload: UpdateFixedDepartureVehicleSupplementRequest;
    }) => api.updateFixedDepartureVehicleSupplement(id, supplementId, payload),
    'Supplement updated',
    'Failed to update the supplement',
  );

export const useDeleteFixedDepartureVehicleSupplement = (id: string) =>
  useChildMutation(
    id,
    (supplementId: string) => api.deleteFixedDepartureVehicleSupplement(id, supplementId),
    'Supplement removed',
    'Failed to remove the supplement',
  );

// ── Hotels ────────────────────────────────────────────────────────────────────

export const useCreateFixedDepartureHotel = (id: string) =>
  useChildMutation(
    id,
    (payload: CreateFixedDepartureHotelRequest) => api.createFixedDepartureHotel(id, payload),
    'Hotel option added',
    'Failed to add the hotel option',
  );

export const useUpdateFixedDepartureHotel = (id: string) =>
  useChildMutation(
    id,
    ({ hotelId, payload }: { hotelId: string; payload: UpdateFixedDepartureHotelRequest }) =>
      api.updateFixedDepartureHotel(id, hotelId, payload),
    'Hotel option updated',
    'Failed to update the hotel option',
  );

export const useDeleteFixedDepartureHotel = (id: string) =>
  useChildMutation(
    id,
    (hotelId: string) => api.deleteFixedDepartureHotel(id, hotelId),
    'Hotel option removed',
    'Failed to remove the hotel option',
  );

export const useReorderFixedDepartureHotels = (id: string) =>
  useChildMutation(
    id,
    (ids: string[]) => api.reorderFixedDepartureHotels(id, ids),
    'Hotel order saved',
    'Failed to reorder the hotel options',
  );

// ── Land package ──────────────────────────────────────────────────────────────

export const useSaveFixedDepartureLandPackage = (id: string) =>
  useChildMutation(
    id,
    (payload: SaveFixedDepartureLandPackageRequest) =>
      api.saveFixedDepartureLandPackage(id, payload),
    'Land package saved',
    'Failed to save the land package',
  );

// ── Flights ───────────────────────────────────────────────────────────────────

export const useCreateFixedDepartureFlight = (id: string) =>
  useChildMutation(
    id,
    (payload: CreateFixedDepartureFlightRequest) => api.createFixedDepartureFlight(id, payload),
    'Flight option added',
    'Failed to add the flight option',
  );

export const useUpdateFixedDepartureFlight = (id: string) =>
  useChildMutation(
    id,
    ({ flightId, payload }: { flightId: string; payload: UpdateFixedDepartureFlightRequest }) =>
      api.updateFixedDepartureFlight(id, flightId, payload),
    'Flight option updated',
    'Failed to update the flight option',
  );

export const useDeleteFixedDepartureFlight = (id: string) =>
  useChildMutation(
    id,
    (flightId: string) => api.deleteFixedDepartureFlight(id, flightId),
    'Flight option removed',
    'Failed to remove the flight option',
  );

export const useReorderFixedDepartureFlights = (id: string) =>
  useChildMutation(
    id,
    (ids: string[]) => api.reorderFixedDepartureFlights(id, ids),
    'Flight order saved',
    'Failed to reorder the flight options',
  );

// ── Inclusions ────────────────────────────────────────────────────────────────

export const useCreateFixedDepartureInclusion = (id: string) =>
  useChildMutation(
    id,
    (payload: CreateFixedDepartureTextRequest) => api.createFixedDepartureInclusion(id, payload),
    'Inclusion added',
    'Failed to add the inclusion',
  );

export const useUpdateFixedDepartureInclusion = (id: string) =>
  useChildMutation(
    id,
    ({ rowId, payload }: { rowId: string; payload: UpdateFixedDepartureTextRequest }) =>
      api.updateFixedDepartureInclusion(id, rowId, payload),
    'Inclusion updated',
    'Failed to update the inclusion',
  );

export const useDeleteFixedDepartureInclusion = (id: string) =>
  useChildMutation(
    id,
    (rowId: string) => api.deleteFixedDepartureInclusion(id, rowId),
    'Inclusion removed',
    'Failed to remove the inclusion',
  );

export const useReorderFixedDepartureInclusions = (id: string) =>
  useChildMutation(
    id,
    (ids: string[]) => api.reorderFixedDepartureInclusions(id, ids),
    'Inclusion order saved',
    'Failed to reorder the inclusions',
  );

// ── Exclusions ────────────────────────────────────────────────────────────────

export const useCreateFixedDepartureExclusion = (id: string) =>
  useChildMutation(
    id,
    (payload: CreateFixedDepartureTextRequest) => api.createFixedDepartureExclusion(id, payload),
    'Exclusion added',
    'Failed to add the exclusion',
  );

export const useUpdateFixedDepartureExclusion = (id: string) =>
  useChildMutation(
    id,
    ({ rowId, payload }: { rowId: string; payload: UpdateFixedDepartureTextRequest }) =>
      api.updateFixedDepartureExclusion(id, rowId, payload),
    'Exclusion updated',
    'Failed to update the exclusion',
  );

export const useDeleteFixedDepartureExclusion = (id: string) =>
  useChildMutation(
    id,
    (rowId: string) => api.deleteFixedDepartureExclusion(id, rowId),
    'Exclusion removed',
    'Failed to remove the exclusion',
  );

export const useReorderFixedDepartureExclusions = (id: string) =>
  useChildMutation(
    id,
    (ids: string[]) => api.reorderFixedDepartureExclusions(id, ids),
    'Exclusion order saved',
    'Failed to reorder the exclusions',
  );

// ── Activities ────────────────────────────────────────────────────────────────

export const useCreateFixedDepartureActivity = (id: string) =>
  useChildMutation(
    id,
    (payload: CreateFixedDepartureActivityRequest) => api.createFixedDepartureActivity(id, payload),
    'Activity added',
    'Failed to add the activity',
  );

export const useUpdateFixedDepartureActivity = (id: string) =>
  useChildMutation(
    id,
    ({
      activityId,
      payload,
    }: {
      activityId: string;
      payload: UpdateFixedDepartureActivityRequest;
    }) => api.updateFixedDepartureActivity(id, activityId, payload),
    'Activity updated',
    'Failed to update the activity',
  );

export const useDeleteFixedDepartureActivity = (id: string) =>
  useChildMutation(
    id,
    (activityId: string) => api.deleteFixedDepartureActivity(id, activityId),
    'Activity removed',
    'Failed to remove the activity',
  );

export const useReorderFixedDepartureActivities = (id: string) =>
  useChildMutation(
    id,
    (ids: string[]) => api.reorderFixedDepartureActivities(id, ids),
    'Activity order saved',
    'Failed to reorder the activities',
  );

// ── Creating an itinerary from a template (ADR-0069) ──────────────────────────

export const useFixedDepartureQuotePreview = (
  id: string | undefined,
  query: FixedDepartureQuotePreviewQuery | null,
) =>
  useQuery({
    queryKey: ['fixed-departures', 'quote', id, query] as const,
    queryFn: () =>
      api.previewFixedDepartureQuote(id as string, query as FixedDepartureQuotePreviewQuery),
    enabled: Boolean(id) && query !== null,
    staleTime: 30_000,
  });

export const useFixedDepartureRoomQuotePreview = (
  id: string | undefined,
  body: FixedDepartureRoomQuotePreviewRequest | null,
) =>
  useQuery({
    queryKey: ['fixed-departures', 'room-quote', id, body] as const,
    queryFn: () =>
      api.previewFixedDepartureRoomQuote(
        id as string,
        body as FixedDepartureRoomQuotePreviewRequest,
      ),
    enabled: Boolean(id) && body !== null,
    staleTime: 30_000,
  });

export const useCreateItineraryFromFixedDeparture = (leadId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateItineraryFromFixedDepartureRequest) =>
      api.createItineraryFromFixedDeparture(leadId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['itineraries'] });
      qc.invalidateQueries({ queryKey: ['leads', 'detail', leadId] });
      toast.success('Fixed departure itinerary created');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to create the itinerary'),
  });
};

export const useReseedFixedDepartureItinerary = (itineraryId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ReseedFixedDepartureItineraryRequest) =>
      api.reseedFixedDepartureItinerary(itineraryId, payload),
    onSuccess: () => {
      // The whole itinerary changed shape, so nothing cached about it survives.
      qc.invalidateQueries({ queryKey: ['itineraries'] });
      qc.invalidateQueries({ queryKey: ['itinerary', itineraryId] });
      toast.success('Itinerary updated from the fixed departure');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update the itinerary'),
  });
};

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
